import React, { useState, useCallback, useEffect } from "react";
import { Form, FloatingLabel, Accordion, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useAxios from "../../services/AxiosConfig";
import inputStyles from '../ManagementPages.module.css';
import styles from './Support.module.css';
import { showAlert, closeAlert } from "../../components/PerinHealthAlertSlice";
import PerinHealthAlert from "../../components/PerinHealthAlert";

const Support = () => {
    const dispatch = useDispatch();
    const perinHealthAlert = useSelector((state) => state.perinHealthAlert);
    const axiosConfig = useSelector((state) => state.axiosConfig);
    const httpService = useAxios(axiosConfig.envURL);
    
    const [validated, setValidated] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const [ticketData, setTicketData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });

    // FAQ data
    const faqData = [
        {
            question: "How do I reset my password?",
            answer: "You can reset your password by clicking on 'Forgot Password' on the login page. You will receive a verification code via email to reset your password."
        },
        {
            question: "I'm having trouble logging in. What should I do?",
            answer: "Please ensure you are using the correct email address and password. If you continue to have issues, try resetting your password or contact support using the form below."
        },
        {
            question: "How do I access my account information?",
            answer: "Once logged in, you can access your account information through the account management section in your dashboard."
        },
        {
            question: "I need technical support. How can I get help?",
            answer: "Please use the support ticket form below to submit your technical support request. Our team will respond to your inquiry as soon as possible."
        },
        {
            question: "What should I do if I encounter an error?",
            answer: "If you encounter an error, please try refreshing the page. If the error persists, please submit a support ticket with details about the error you're experiencing, including any error messages and what you were doing when it occurred."
        },
        {
            question: "How long does it take to receive a response?",
            answer: "We aim to respond to all support tickets within 24-48 hours during business days. For urgent matters, please indicate the urgency in your ticket subject line."
        }
    ];

    const handleChange = useCallback((e) => {
        const { id, value } = e.target;
        setTicketData(prevData => ({
            ...prevData,
            [id]: value
        }));
    }, []);

    const showErrorAlert = useCallback((header, message) => {
        dispatch(showAlert({
            header,
            message,
            type: 'danger',
            dismissable: true
        }));
    }, [dispatch]);

    const showSuccessAlert = useCallback((header, message) => {
        dispatch(showAlert({
            header,
            message,
            type: 'success',
            dismissable: false
        }));
    }, [dispatch]);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        const form = e.currentTarget;

        if (form.checkValidity() === false) {
            e.stopPropagation();
            setValidated(true);
            return;
        }

        setValidated(false);
        setIsLoading(true);

        try {
            const response = await httpService.request({
                method: 'POST',
                headers: { "Content-Type": "application/json" },
                url: 'support/ticket',
                data: JSON.stringify(ticketData),
            });

            if (response) {
                setSubmitted(true);
                setTicketData({
                    name: '',
                    email: '',
                    subject: '',
                    message: ''
                });
                showSuccessAlert(
                    "Success",
                    "Your support ticket has been submitted successfully. We will respond to your inquiry as soon as possible."
                );
                // Scroll to top to show success message
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        } catch (error) {
            console.error('Error submitting support ticket:', error);
            const errorInfo = error.response?.data;
            const errorMessage = errorInfo?.message || 
                "There was an error submitting your support ticket. Please try again.";
            showErrorAlert("Error", errorMessage);
        } finally {
            setIsLoading(false);
        }
    }, [ticketData, httpService, showErrorAlert, showSuccessAlert]);

    // Auto-dismiss success message after 5 seconds
    useEffect(() => {
        if (submitted) {
            const timer = setTimeout(() => {
                setSubmitted(false);
            }, 5000); // 5 seconds

            return () => clearTimeout(timer);
        }
    }, [submitted]);

    // Auto-dismiss top alert after 5 seconds
    useEffect(() => {
        if (perinHealthAlert.show) {
            const timer = setTimeout(() => {
                dispatch(closeAlert());
            }, 5000); // 5 seconds

            return () => clearTimeout(timer);
        }
    }, [perinHealthAlert.show, dispatch]);

    return (
        <div className={styles.container}>
            {perinHealthAlert.show && (
                <PerinHealthAlert
                    show={perinHealthAlert.show}
                    type={perinHealthAlert.type}
                    header={perinHealthAlert.header}
                    message={perinHealthAlert.message}
                    dismissable={perinHealthAlert.dismissable}
                />
            )}
            
            <div className={styles.innerContainer}>
                {/* Header Section */}
                <div className={styles.headerSection}>
                    <h1 className={styles.headerTitle}>
                        Support Center
                    </h1>
                    <p className={styles.headerSubtitle}>
                        Find answers to common questions or submit a support ticket
                    </p>
                </div>

                {/* FAQ Section */}
                <div className={styles.faqSection}>
                    <h2 className={styles.sectionTitle}>
                        Frequently Asked Questions
                    </h2>
                    <Accordion defaultActiveKey="0" flush>
                        {faqData.map((faq, index) => (
                            <Accordion.Item 
                                eventKey={index.toString()} 
                                key={index}
                                className={index < faqData.length - 1 ? styles.accordionItem : styles.accordionItemLast}
                            >
                                <Accordion.Header className={styles.accordionHeader}>
                                    <strong className={styles.accordionQuestion}>
                                        {faq.question}
                                    </strong>
                                </Accordion.Header>
                                <Accordion.Body className={styles.accordionBody}>
                                    {faq.answer}
                                </Accordion.Body>
                            </Accordion.Item>
                        ))}
                    </Accordion>
                </div>

                {/* Support Ticket Form */}
                <div className={styles.formSection}>
                    <h2 className={styles.formSectionTitle}>
                        Submit a Support Ticket
                    </h2>
                    <p className={styles.sectionDescription}>
                        Can't find what you're looking for? Submit a support ticket and our team will get back to you as soon as possible.
                    </p>

                        {submitted && (
                            <div className={styles.successMessage}>
                                <FontAwesomeIcon icon="fa-solid fa-check-circle" className={styles.successIcon} />
                                <div>
                                    <strong>Thank you!</strong> Your support ticket has been submitted successfully. We'll respond as soon as possible.
                                </div>
                            </div>
                        )}

                        <Form 
                            onSubmit={handleSubmit} 
                            noValidate 
                            validated={validated}
                        >
                            <div className={styles.formGrid}>
                                <Form.Group>
                                    <FloatingLabel label="Your Name *">
                                        <Form.Control
                                            id="name"
                                            type="text"
                                            value={ticketData.name}
                                            placeholder="Your Name"
                                            className={`${inputStyles.BorderedComponent} ${styles.formControl}`}
                                            onChange={handleChange}
                                            required
                                            disabled={isLoading}
                                            maxLength={255}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            Please provide your name.
                                        </Form.Control.Feedback>
                                    </FloatingLabel>
                                </Form.Group>

                                <Form.Group>
                                    <FloatingLabel label="Your Email *">
                                        <Form.Control
                                            id="email"
                                            type="email"
                                            value={ticketData.email}
                                            placeholder="Your Email"
                                            className={`${inputStyles.BorderedComponent} ${styles.formControl}`}
                                            onChange={handleChange}
                                            required
                                            disabled={isLoading}
                                            maxLength={255}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            Please provide a valid email address.
                                        </Form.Control.Feedback>
                                    </FloatingLabel>
                                </Form.Group>
                            </div>

                            <Form.Group className={`mb-3 ${styles.fullWidthField}`}>
                                <FloatingLabel label="Subject *">
                                    <Form.Control
                                        id="subject"
                                        type="text"
                                        value={ticketData.subject}
                                        placeholder="Subject"
                                        className={`${inputStyles.BorderedComponent} ${styles.formControl}`}
                                        onChange={handleChange}
                                        required
                                        disabled={isLoading}
                                        maxLength={500}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        Please provide a subject.
                                    </Form.Control.Feedback>
                                </FloatingLabel>
                            </Form.Group>

                            <Form.Group className={`mb-4 ${styles.fullWidthField}`}>
                                <FloatingLabel label="Message *">
                                    <Form.Control
                                        id="message"
                                        as="textarea"
                                        value={ticketData.message}
                                        placeholder="Please describe your issue or question..."
                                        className={`${inputStyles.BorderedComponent} ${styles.textareaControl}`}
                                        onChange={handleChange}
                                        required
                                        disabled={isLoading}
                                        maxLength={5000}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        Please provide a message.
                                    </Form.Control.Feedback>
                                </FloatingLabel>
                            </Form.Group>

                            <button 
                                type="submit"
                                disabled={isLoading}
                                className={styles.submitButton}
                            >
                                {isLoading ? (
                                    <span className={styles.loadingContainer}>
                                        <Spinner animation="border" size="sm" role="status" aria-hidden="true" />
                                        Submitting...
                                    </span>
                                ) : (
                                    "Submit Support Ticket"
                                )}
                            </button>
                        </Form>
                    </div>
                </div>
            </div>
    );
};

export default Support;
